<template>
  <tr
    class="rf-row group/row hover:bg-beige-0 transition-background relative h-32 duration-300 ease-in-out [&_td]:p-4 [&_td]:align-middle [&_td_>_*]:mx-auto"
    data-cy="segment-row"
    :class="{ 'bg-beige-10!': selected }"
  >
    <td class="pr-0! whitespace-nowrap" style="min-width: calc(24px + 1rem)">
      <RfCheckbox
        data-cy="segment-row--checkbox"
        :data-cy-value="selected"
        class="rf-row--checkbox py-1 opacity-0 transition-opacity duration-300 ease-in-out group-hover/row:opacity-100!"
        :class="{ 'rf-row--checkbox-selected opacity-100': selected }"
        :modelValue="selected"
        @update:modelValue="emit(selected ? 'deselect' : 'select', segment.id)"
      />
    </td>
    <td class="5xl:px-7! px-5! whitespace-nowrap">
      <span class="5xl:justify-start! flex items-center justify-center! gap-0.5">
        <RfMenu
          preset="promptStatus"
          class="5xl:w-27 w-11 justify-start!"
          data-cy="segment-status--menu"
          :disabled="disabled || segment.is_locked || segment.segment_type === 'pinpoint'"
        >
          <template #activator>
            <span class="inline-flex items-center gap-2">
              <RfStatus
                data-cy="segment-row--status"
                :data-cy-value="segment.is_enabled"
                :isEnabled="segment.is_enabled"
              />
              <span class="text-body 5xl:inline-block text-grey-1 hidden">
                {{ segment.is_enabled ? "Enabled" : "Disabled" }}
              </span>
            </span>
          </template>
          <template #menu="{ close }">
            <button
              class="flex w-full items-center gap-3 px-4 py-2 first:pt-4! last:pb-4!"
              :data-cy="`segment-status--${segment.is_enabled ? 'disable' : 'enable'}`"
              @click="
                emit('status', !segment.is_enabled);
                close();
              "
            >
              <RfStatus :isEnabled="!segment.is_enabled" />
              {{ segment.is_enabled ? "Disable" : "Enable" }}
            </button>
          </template>
        </RfMenu>
      </span>
    </td>
    <td class="pl-0!">
      <div class="flex items-center gap-5">
        <span class="flex flex-col gap-1">
          <div class="flex items-start gap-2">
            <RouterLink
              class="text-action-buttons line-clamp-1 font-bold! text-ellipsis text-blue-50"
              :title="segment.name"
              :to="getSegmentLink(segment.id, $route.params.aid)"
            >
              {{ segment.name }}
            </RouterLink>
            <button
              v-if="!disabled && !segment.is_locked"
              class="rf-row--edit-inline flex items-center gap-3 opacity-0 transition-opacity duration-300 ease-in-out group-hover/row:opacity-100!"
              @click="emit('edit')"
            >
              <RfEditIcon class="fill-beige-50 h-5! w-5!" />
            </button>
          </div>
          <span v-if="segment.description" class="text-body text-grey-1">
            {{ segment.description }}
          </span>
        </span>
      </div>
    </td>
    <td>
      <div class="rf-row--configuration ml-0! flex w-full flex-col items-start">
        <div class="flex items-start justify-center gap-2">
          <div class="flex-1">
            <div
              v-for="filter in displayFilters.slice(0, 3)"
              :key="filter[0]"
              class="text-body text-grey-1"
            >
              {{ filter[0] }}{{ filter[1] ? ":" : "" }}
              <span class="text-black-2">{{ filter[1] }}</span>
            </div>
          </div>
          <div v-if="displayFilters.length > 3" class="5xl:block hidden flex-1">
            <div
              v-for="filter in displayFilters.slice(3, 6)"
              :key="filter[0]"
              class="text-body text-grey-1"
            >
              {{ filter[0] }}{{ filter[1] ? ":" : "" }}
              <span class="text-black-2">{{ filter[1] }}</span>
            </div>
          </div>
        </div>

        <span ref="tooltipAnchorEl">
          <template
            v-for="b in [
              { length: 6, class: '5xl:inline-block! hidden!' },
              { length: 3, class: 'inline-block! 5xl:hidden!' },
            ]"
            :key="b.class"
          >
            <button
              v-if="displayFilters.length > b.length"
              :class="b.class"
              @focus="() => tooltipEl?.show?.()"
              @mouseenter="() => tooltipEl?.show?.()"
              @blur="() => tooltipEl?.hide?.()"
              @mouseleave="() => tooltipEl?.hide?.()"
            >
              <RfSeeMoreIcon
                class="rf-row--see-more bg-input-background group-hover/row:bg-white-1! z-10 h-6! w-6! rounded-full"
              />
            </button>
          </template>
        </span>

        <RfTooltip
          ref="tooltipEl"
          :anchor="tooltipAnchorEl"
          class="rf-row--configuration-tooltip mt-2 w-fit"
        >
          <template #tooltip>
            <div
              v-for="filter in displayFilters"
              :key="filter[0]"
              class="text-body text-grey-1 w-64"
            >
              {{ filter[0] }}{{ filter[1] ? ":" : "" }}
              <span class="text-black-2">{{ filter[1] }}</span>
            </div>
          </template>
        </RfTooltip>
      </div>
    </td>
    <td class="px-4! text-center whitespace-nowrap">
      <span class="text-body text-black-2">
        {{ dayjs(segment.created_at).format(`${dateOrder()}/YY`) }}
      </span>
    </td>
    <td class="text-right">
      <span class="text-body text-decoration-none text-black-2">
        {{ toLocaleNumberString(props.segment.paths.length) }}
      </span>
    </td>
    <td class="bg-goal text-right">
      <RfTooltip v-if="!currTotalUsers" class="mr-0! w-fit">
        <template #activator> <RfHelpIcon class="h-6! w-6!" /> </template>
        <template #tooltip>
          <div class="w-64">
            There is no data for this period of time. Try selecting a different time range or make
            sure the prompt has been active.
          </div>
        </template>
      </RfTooltip>
      <span v-else class="text-body text-decoration-none text-black-2">
        {{ toLocaleNumberString(currTotalUsers) }}
      </span>
    </td>
    <td class="px-2! whitespace-nowrap">
      <div class="flex items-center justify-center">
        <RfMenu
          v-if="!disabled && !isSystemSegment"
          data-cy="segment-row--menu"
          hideIcon
          preset="promptMenu"
        >
          <template #activator="{ opened }">
            <RfDotsIcon
              class="rf-row--menu fill-beige-50 m-auto flex h-6! w-6! opacity-0 transition-opacity duration-300 ease-in-out group-hover/row:opacity-100!"
              :class="{ 'rf-row--menu-opened opacity-100!': opened }"
            />
          </template>
          <template #menu="{ close }">
            <button
              v-if="!segment.is_locked"
              class="flex w-full items-center gap-3 px-4 py-2 first:pt-4!"
              data-cy="segment-row--menu-edit"
              @click="
                emit('edit');
                close();
              "
            >
              <RfEditIcon class="fill-beige-50 h-6! w-6!" />
              Edit
            </button>
            <button
              v-if="!isSystemSegment"
              class="flex w-full items-center gap-3 px-4 py-2 first:pt-4! last:pb-4!"
              data-cy="segment-row--menu-clone"
              @click="
                emit('clone');
                close();
              "
            >
              <RfCloneIcon class="fill-beige-50 h-6! w-6!" />
              Clone
            </button>
            <button
              v-if="!segment.is_locked"
              class="flex w-full items-center gap-3 px-4 py-2 last:pb-4!"
              data-cy="segment-row--menu-delete"
              @click="
                emit('delete');
                close();
              "
            >
              <RfDeleteIcon class="fill-beige-50 h-6! w-6!" />
              Delete
            </button>
          </template>
        </RfMenu>
      </div>
    </td>
  </tr>
</template>

<script setup>
import RfCloneIcon from "@/components/icons/RfCloneIcon.vue";
import RfDeleteIcon from "@/components/icons/RfDeleteIcon.vue";
import RfDotsIcon from "@/components/icons/RfDotsIcon.vue";
import RfEditIcon from "@/components/icons/RfEditIcon.vue";
import RfCheckbox from "@/components/inputs/RfCheckbox.vue";
import RfMenu from "@/components/menus/RfMenu.vue";
import { getSegmentLink } from "@/utils/getLink";
import RfStatus from "@/blocks/RfSegments/RfStatus.vue";
import RfTooltip from "@/components/tooltip/RfTooltip.vue";
import RfHelpIcon from "@/components/icons/RfHelpIcon.vue";
import { computed, ref } from "vue";
import RfSeeMoreIcon from "@/components/icons/RfSeeMoreIcon.vue";
import dayjs from "dayjs";
import { toLocaleNumberString } from "@/utils/stringHelpers";
import { dateOrder } from "@/utils/DateDisplayUtils";
import { RouterLink } from "vue-router";
import { getDisplayFilters } from "@/utils/segmentsHelpers";

const emit = defineEmits(["select", "deselect", "edit", "clone", "delete", "status"]);

const props = defineProps({
  segment: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  timePeriod: { type: String, default: null },
  disabled: { type: [Boolean, String], default: false },
  timezone: { type: String, default: "" },
  filters: { type: Object, default: () => ({}) },
  customDevices: { type: Array, default: () => [] },
});

const tooltipEl = ref();
const tooltipAnchorEl = ref();

const currTotalUsers = computed(() => props.segment?.data?.users?.[props.timePeriod]?.uniques || 0);

const displayFilters = computed(() => getDisplayFilters(props.filters, props.customDevices));

const isSystemSegment = computed(() => props.segment?.segment_type?.startsWith("system_"));
</script>

<style lang="scss" scoped>
:deep(.rf-row--configuration-tooltip .rf-tooltip) {
  max-width: 350px;
}
</style>
